// #4401: how many boots in a row have ended in a watchdog reload, remembered across the
// reload itself.
//
// The splash watchdog already refuses to reload twice in one browser session. That stops
// a loop inside a single page, and it does nothing about the pattern a customer actually
// lived through: boot, wedge, reload, wedge again, reload again — for over an hour. Each
// reload starts a fresh session, so the existing guard is fresh too.
//
// So the count is kept in localStorage, which is a different storage system from the one
// that is jammed and keeps working when it is. Past the limit, the watchdog stops
// reloading and the user is told what is actually wrong.
//
// Cleared as soon as a boot finishes normally, so a device that recovers is not carrying
// a grudge from last week.
//
// Injectable storage, no Angular: the unit test drives it with a plain object.

/** The one localStorage key this module owns. */
export const WEDGED_BOOT_COUNT_KEY = 'cs_wedged_boot_count';

/** The subset of localStorage this needs — injected so a test can supply a plain object. */
export interface CountStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * How many boots have ended in a watchdog reload on this device.
 *
 * Anything unreadable — no storage at all, a missing key, a value somebody or something
 * else wrote — counts as ZERO. That direction is deliberate: an unreadable count must
 * never send a user who is booting perfectly well to an error screen, and the cost of
 * guessing low is only that the app tries a reload it has already tried.
 */
export function readWedgedBootCount(storage: CountStorage | undefined | null): number {
  if (!storage) return 0;
  try {
    const raw = storage.getItem(WEDGED_BOOT_COUNT_KEY);
    if (raw == null) return 0;
    const value = Number.parseInt(raw, 10);
    if (!Number.isFinite(value) || value < 0) return 0;
    return value;
  } catch {
    // a browser can refuse localStorage outright (private mode, blocked site data). That
    // is not something to report from here — it is not a fault, and this is a counter.
    return 0;
  }
}

/** Record that another boot ended in a watchdog reload, and return the new count. */
export function recordWedgedBoot(storage: CountStorage | undefined | null): number {
  const next = readWedgedBootCount(storage) + 1;
  if (!storage) return next;
  try {
    storage.setItem(WEDGED_BOOT_COUNT_KEY, String(next));
  } catch {
    // nothing to do: the count is a convenience, and a device that cannot remember it
    // simply keeps the behaviour it had before #4401
  }
  return next;
}

/**
 * Forget the count, because a boot finished normally.
 *
 * Called on every successful boot rather than only after a recovery, so the count always
 * means "reloads since the app last worked" and can never accumulate across weeks of
 * healthy use into an error screen nobody deserves.
 */
export function clearWedgedBootCount(storage: CountStorage | undefined | null): void {
  if (!storage) return;
  try {
    storage.removeItem(WEDGED_BOOT_COUNT_KEY);
  } catch {
    // same as above — a storage that refuses to forget costs one extra error screen at
    // worst, and there is nothing useful to do about it here
  }
}
